import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import { latestNotebookRevision } from "./reportRecipe"
import type { Notebook } from "./reportTypes"

export class ReportRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = "ReportRequestError"
    this.status = status
  }
}

export function createReportRequest(
  organizationSlug: string,
  workspaceId: string,
) {
  return async function request<T>(
    path: string,
    body?: unknown,
    method = "POST",
    signal?: AbortSignal,
  ): Promise<T> {
    const url = new URL(
      `/api/sofia/notebook-reports${path.replace(/^\/api/, "")}`,
      window.location.origin,
    )
    url.searchParams.set("organization", organizationSlug)
    url.searchParams.set("workspace", workspaceId)
    const headers = await getOrganizationBearerHeaders(organizationSlug)
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError")
    }
    const response = await fetch(url.toString(), {
      method: body === undefined ? "GET" : method,
      signal,
      cache: "no-store",
      headers: {
        ...headers,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    const payload: unknown = await response.json().catch(() => null)
    if (!response.ok) {
      const object =
        payload && typeof payload === "object"
          ? (payload as Record<string, unknown>)
          : null
      const message = object?.error ?? object?.detail
      throw new ReportRequestError(
        typeof message === "string"
          ? message
          : `Request failed (${response.status}).`,
        response.status,
      )
    }
    return payload as T
  }
}

/** Saves a notebook's content as its next revision, guarded by the revision it replaces. */
export async function saveNotebookRevision(
  request: ReturnType<typeof createReportRequest>,
  notebook: Pick<Notebook, "id" | "title" | "summary" | "sections">,
  expectedRevision: number,
  signal?: AbortSignal,
): Promise<Notebook> {
  return unpackNotebook(
    await request<Notebook | { notebook: Notebook }>(
      `/api/notebooks/${encodeURIComponent(notebook.id)}`,
      {
        title: notebook.title,
        summary: notebook.summary,
        sections: notebook.sections,
        expected_revision: expectedRevision,
      },
      "PUT",
      signal,
    ),
  )
}

/**
 * Saves a new title as the notebook's next revision. When the save is rejected as a revision conflict (409) and a newer revision exists, that revision is handed to `onNewer` so a retry renames it instead of resubmitting the stale one. Pass `undefined` when replacing the open notebook would discard unsaved edits.
 *
 * @returns The saved notebook.
 */
export async function saveNotebookTitle(
  request: ReturnType<typeof createReportRequest>,
  notebook: Pick<Notebook, "id" | "title" | "summary" | "sections">,
  title: string,
  expectedRevision: number,
  onNewer: ((latest: Notebook) => void) | undefined,
  signal?: AbortSignal,
): Promise<Notebook> {
  try {
    return await saveNotebookRevision(
      request,
      { ...notebook, title },
      expectedRevision,
      signal,
    )
  } catch (failure: unknown) {
    if (
      !onNewer ||
      !(failure instanceof ReportRequestError) ||
      failure.status !== 409
    ) {
      throw failure
    }
    const latest = await request<Notebook | { notebook: Notebook }>(
      `/api/notebooks/${encodeURIComponent(notebook.id)}`,
      undefined,
      "GET",
      signal,
    )
      .then(unpackNotebook)
      .catch(() => null)
    if (
      !latest ||
      latestNotebookRevision(latest) <= expectedRevision ||
      signal?.aborted
    ) {
      throw failure
    }
    onNewer(latest)
    throw new Error(
      `${reportErrorText(failure)} The latest revision is now open; save again to rename it.`,
    )
  }
}

export function unpackNotebook(
  payload: Notebook | { notebook: Notebook },
): Notebook {
  return "notebook" in payload ? payload.notebook : payload
}

export function formatDate(
  value: string | undefined,
  withTime = false,
  withSeconds = false,
): string {
  if (!value) {
    return "—"
  }
  // UTC noon stays on the same Pacific calendar day regardless of browser timezone.
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    ...(withTime
      ? {
          hour: "numeric",
          minute: "2-digit",
          ...(withSeconds ? { second: "2-digit" } : {}),
        }
      : {}),
  }).format(date)
}

export function reportErrorText(error: unknown): string {
  return error instanceof Error && error.message
    ? error.message
    : "Something went wrong. Please try again."
}
